import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * FeedChatLauncher
 *
 * Icon-only bell-neighbour in the slate header that lets the user slice
 * their feed by a recent time window and either:
 *
 *   1. open a `CollectionChatSidebar` chat grounded in that window, or
 *   2. copy that window's captures to the clipboard as a JSON paste-blob
 *      (same `summarizeBlockForContext` shape used by the Collection +
 *      smart-collection headers — see `@/lib/copy-context`).
 *
 * The launcher lives globally in `AppNav` so the affordance is reachable
 * from every screen, not only the home stream. The mounted chat sidebar
 * is therefore owned by this component, not by the consuming screen.
 *
 * Block resolution differs by action:
 *
 *   - Chat: a single `listBlocks` call with `limit:
 *     MAX_COLLECTION_CHAT_BLOCK_IDS` (50). The 50-row ceiling is
 *     server-enforced — `CollectionChatRequestSchema` rejects more, and
 *     the LLM-context budget on `/assistant/collection-chat` is sized
 *     around that. Bumping this requires server changes; for now the
 *     chat reads the top-50 most-recent rows in the window.
 *
 *   - Copy: paginates the window in `FEED_COPY_PAGE_SIZE`-sized batches
 *     (the per-request cap enforced by `BlockFilterSchema.limit.max(250)`)
 *     until the window is exhausted. There's no content cap — the
 *     paste blob carries every capture in the bucket. A high runaway
 *     ceiling (`MAX_FEED_COPY_BLOCKS`) is in place purely to terminate
 *     the loop if the server returns surprising results; real usage
 *     never approaches it.
 *
 * Both actions order by `capturedAt` desc — not `surfaceAt`. `surfaceAt`
 * moves forward on reprocess/re-surface, which would bleed old blocks
 * back into a "last N days" window. `capturedAt` is the truthful column
 * for "things I saved recently."
 *
 * We deliberately bypass `/assistant/query` (the omnibar's recall path)
 * for both actions: that endpoint is top-K hybrid search, not a time
 * filter. `listBlocks` returns the actual slice.
 */
import React, { memo, useCallback, useState } from "react";
import { faMessage, faSpinnerThird, } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { Popover, PopoverContent, PopoverTrigger, } from "@shared/client/components/ui/popover/popover";
import { showToast } from "@shared/client/components/toast/show-toast";
import { MAX_COLLECTION_CHAT_BLOCK_IDS } from "@shared/types/collection-chat";
import { listBlocks } from "@/api/query-api";
import { CollectionChatSidebar } from "@/components/collection-chat-sidebar/collection-chat-sidebar";
import { summarizeBlockForContext } from "@/lib/copy-context";
import { ConfiguredLogger } from "@/lib/logger";
import "./feed-chat-launcher.scss";
// Logged via ConfiguredLogger (info-level: dev console only, never
// reaches Sentry, dropped entirely in prod builds — see `@/lib/logger`),
// so the payload tree is inspectable in DevTools when copy or download
// fires without polluting prod logs or noising Sentry with PII.
const logger = new ConfiguredLogger({ caller: "FeedChatLauncher" });
const BUCKETS = [
    { id: "7d", label: "Last 7 days", days: 7 },
    { id: "30d", label: "Last 30 days", days: 30 },
    { id: "90d", label: "Last 90 days", days: 90 },
];
// Download reaches one bucket deeper than chat + copy. It's the
// "archive a big slice to a file" path, so a longer window is useful
// there; chat (50-row server cap) and copy (clipboard blob) gain
// nothing from going past 90 days, so they stay on the shared presets.
const DOWNLOAD_BUCKETS = [
    ...BUCKETS,
    { id: "180d", label: "Last 180 days", days: 180 },
];
const MS_PER_DAY = 1000 * 60 * 60 * 24;
/**
 * Per-request cap on `/block/list`, enforced server-side by Zod
 * (`BlockFilterSchema.limit.max(250)`). We use the ceiling for copy
 * so paginating a full 90-day window costs as few round-trips as
 * possible.
 */
const FEED_COPY_PAGE_SIZE = 250;
/**
 * Runaway safety net for the windowed pagination loop shared by copy
 * and download. The natural exit is "page returned fewer rows than
 * `FEED_COPY_PAGE_SIZE`" (window exhausted); this constant is only hit
 * if the server somehow keeps returning full pages indefinitely. Sized
 * well beyond any plausible window for a heavy user — download reaches
 * the deepest, a 180-day window (~100 captures/day × 180d = 18000) —
 * so real usage never reaches it and no user-visible truncation
 * happens; `truncated: true` in the payload effectively flags a server
 * bug, not a content cap.
 */
const MAX_FEED_COPY_BLOCKS = 40_000;
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const FeedChatLauncher = memo(() => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(undefined);
    const [chatScope, setChatScope] = useState(undefined);
    /**
     * Single-page fetch for chat — bounded by the server's
     * `MAX_COLLECTION_CHAT_BLOCK_IDS` (50) cap, which is what the
     * `/assistant/collection-chat` endpoint will accept. Loading more
     * here would just be discarded server-side.
     */
    const fetchBlocksForChat = useCallback(async (bucket) => {
        const after = new Date(Date.now() - bucket.days * MS_PER_DAY).toISOString();
        return listBlocks({
            capturedAfter: after,
            limit: MAX_COLLECTION_CHAT_BLOCK_IDS,
            orderBy: "capturedAt",
            order: "desc",
        });
    }, []);
    /**
     * Paginated fetch for copy — walks the window in
     * `FEED_COPY_PAGE_SIZE` batches until the server reports the window
     * is exhausted (a page returned fewer rows than `FEED_COPY_PAGE_SIZE`)
     * or the runaway safety net trips. Stable cursor: `(capturedAt
     * desc, id desc)` is the server-side order, so paging by `offset`
     * doesn't skip / double-count rows for a stationary window.
     */
    const fetchAllBlocksInWindow = useCallback(async (bucket) => {
        const after = new Date(Date.now() - bucket.days * MS_PER_DAY).toISOString();
        const out = [];
        let offset = 0;
        while (out.length < MAX_FEED_COPY_BLOCKS) {
            const data = await listBlocks({
                capturedAfter: after,
                limit: FEED_COPY_PAGE_SIZE,
                offset,
                orderBy: "capturedAt",
                order: "desc",
            });
            out.push(...data.blocks);
            // Window-exhausted exit: the server returned a short page,
            // so there are no more rows within the time filter.
            if (data.blocks.length < FEED_COPY_PAGE_SIZE)
                break;
            offset += FEED_COPY_PAGE_SIZE;
        }
        // Trim to the safety-net ceiling in the (effectively impossible)
        // case the last full page pushed us past it.
        return out.slice(0, MAX_FEED_COPY_BLOCKS);
    }, []);
    const handleChat = useCallback(async (bucket) => {
        if (pendingAction !== undefined)
            return;
        setPendingAction(`${bucket.id}:chat`);
        try {
            const data = await fetchBlocksForChat(bucket);
            if (data.blocks.length === 0) {
                showToast.warning(`No captures in the ${bucket.label.toLowerCase()}.`);
                return;
            }
            const blockIds = data.blocks.map((b) => b.block.id);
            // One-sided cap signal: `>=` MAX means there MAY be more rows
            // beyond the cutoff. We don't pay for a second query to confirm
            // — the "+" suffix communicates the ceiling honestly without
            // overclaiming the count.
            const labelSuffix = blockIds.length >= MAX_COLLECTION_CHAT_BLOCK_IDS
                ? ` · ${blockIds.length}+ captures`
                : ` · ${blockIds.length} ${blockIds.length === 1 ? "capture" : "captures"}`;
            setChatScope({
                blockIds,
                title: `${bucket.label}${labelSuffix}`,
            });
            setIsPopoverOpen(false);
        }
        catch (e) {
            showToast.error(e instanceof Error ? e.message : "Failed to load captures");
        }
        finally {
            setPendingAction(undefined);
        }
    }, [fetchBlocksForChat, pendingAction]);
    /**
     * Shared build step for copy + download — both materialise the same
     * `feed-window` payload, just route the resulting JSON to different
     * sinks. Returns `undefined` for an empty window so the caller can
     * short-circuit with a "no captures" toast without further work.
     *
     * `truncated` only flips to true when the runaway safety net inside
     * `fetchAllBlocksInWindow` trips — in normal use the pagination
     * loop exits via the window-exhausted branch and the payload holds
     * every capture in the bucket.
     */
    const buildFeedWindowPayload = useCallback(async (bucket) => {
        const blocks = await fetchAllBlocksInWindow(bucket);
        if (blocks.length === 0)
            return undefined;
        const blockSummaries = blocks.map(summarizeBlockForContext);
        const now = new Date();
        const after = new Date(now.getTime() - bucket.days * MS_PER_DAY);
        const truncated = blocks.length >= MAX_FEED_COPY_BLOCKS;
        const payload = {
            type: "feed-window",
            label: bucket.label,
            capturedAfter: after.toISOString(),
            capturedBefore: now.toISOString(),
            blockCount: blockSummaries.length,
            truncated,
            blocks: blockSummaries,
        };
        const json = JSON.stringify(payload, undefined, 2);
        return { payload, json, blockCount: blockSummaries.length, truncated };
    }, [fetchAllBlocksInWindow]);
    const handleCopy = useCallback(async (bucket) => {
        if (pendingAction !== undefined)
            return;
        setPendingAction(`${bucket.id}:copy`);
        try {
            const result = await buildFeedWindowPayload(bucket);
            if (result === undefined) {
                showToast.warning(`No captures in the ${bucket.label.toLowerCase()}.`);
                return;
            }
            // `info` (not `debug`) so the payload tree shows up in the dev
            // console at the default verbosity level. The logger drops
            // `info` in prod builds, so this stays a dev-only inspector
            // hook — no leak of capture data to production console.
            logger.info("Copy payload", {
                bucket: bucket.id,
                blockCount: result.blockCount,
                truncated: result.truncated,
                payload: result.payload,
            });
            await navigator.clipboard.writeText(result.json);
            const ceilingMark = result.truncated ? "+" : "";
            showToast.success(`Copied ${result.blockCount}${ceilingMark} ${result.blockCount === 1 ? "capture" : "captures"} from ${bucket.label.toLowerCase()}.`);
            setIsPopoverOpen(false);
        }
        catch (e) {
            showToast.error(e instanceof Error ? e.message : "Failed to copy captures");
        }
        finally {
            setPendingAction(undefined);
        }
    }, [buildFeedWindowPayload, pendingAction]);
    /**
     * Download — same payload as copy, but offered as a `.json` file
     * via the Blob + anchor-click pattern instead of pasted into the
     * clipboard. For large windows (thousands of captures) the file is
     * easier to inspect / archive / hand to an LLM than a clipboard
     * blob the OS may choke on.
     *
     * `URL.revokeObjectURL` runs synchronously after `a.click()`
     * because the browser has already kicked off the download by the
     * time `click()` returns — keeping the object URL alive any longer
     * would just leak it.
     */
    const handleDownload = useCallback(async (bucket) => {
        if (pendingAction !== undefined)
            return;
        setPendingAction(`${bucket.id}:download`);
        try {
            const result = await buildFeedWindowPayload(bucket);
            if (result === undefined) {
                showToast.warning(`No captures in the ${bucket.label.toLowerCase()}.`);
                return;
            }
            logger.info("Download payload", {
                bucket: bucket.id,
                blockCount: result.blockCount,
                truncated: result.truncated,
                payload: result.payload,
            });
            // Filename: `putty-last-30-days-2026-05-19.json` — the date
            // suffix is the file's *creation* day so multiple downloads
            // of the same bucket on the same day collide (browser will
            // disambiguate with `(1)`/`(2)` suffixes), but cross-day
            // downloads stay distinguishable.
            const dateStamp = new Date().toISOString().slice(0, 10);
            const labelSlug = bucket.label.toLowerCase().replaceAll(/\s+/g, "-");
            const filename = `putty-${labelSlug}-${dateStamp}.json`;
            const blob = new Blob([result.json], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
            const ceilingMark = result.truncated ? "+" : "";
            showToast.success(`Downloaded ${result.blockCount}${ceilingMark} ${result.blockCount === 1 ? "capture" : "captures"} as ${filename}.`);
            setIsPopoverOpen(false);
        }
        catch (e) {
            showToast.error(e instanceof Error ? e.message : "Failed to download captures");
        }
        finally {
            setPendingAction(undefined);
        }
    }, [buildFeedWindowPayload, pendingAction]);
    return (_jsxs(_Fragment, { children: [_jsxs(Popover, { open: isPopoverOpen, onOpenChange: setIsPopoverOpen, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsx("button", { type: "button", className: "feed-chat-launcher__trigger", "aria-label": "Chat with or copy a slice of your feed", "aria-haspopup": "menu", "aria-expanded": isPopoverOpen, title: "Chat with feed", children: _jsx(FaIcon, { icon: faMessage, className: "feed-chat-launcher__trigger-icon" }) }) }), _jsxs(PopoverContent, { className: "feed-chat-launcher__panel", side: "bottom", align: "end", children: [_jsx("div", { className: "feed-chat-launcher__intro", children: "Slice your feed by a recent window. Chats and copy use the same slice \u2014 exactly what you captured, not search results." }), _jsx(BucketSection, { heading: "Chat with", buckets: BUCKETS, pendingAction: pendingAction, action: "chat", onSelect: handleChat }), _jsx("div", { className: "feed-chat-launcher__divider" }), _jsx(BucketSection, { heading: "Copy as JSON", buckets: BUCKETS, pendingAction: pendingAction, action: "copy", onSelect: handleCopy }), _jsx("div", { className: "feed-chat-launcher__divider" }), _jsx(BucketSection, { heading: "Download as JSON", buckets: DOWNLOAD_BUCKETS, pendingAction: pendingAction, action: "download", onSelect: handleDownload })] })] }), chatScope !== undefined ? (_jsx(CollectionChatSidebar, { blockIds: chatScope.blockIds, collectionTitle: chatScope.title, onClose: () => setChatScope(undefined) })) : undefined] }));
});
const BucketSection = memo(({ heading, buckets, pendingAction, action, onSelect, }) => {
    return (_jsxs("div", { className: "feed-chat-launcher__section", children: [_jsx("div", { className: "feed-chat-launcher__section-header", children: heading }), buckets.map((bucket) => {
                const isPending = pendingAction === `${bucket.id}:${action}`;
                const isDisabled = pendingAction !== undefined;
                return (_jsxs("button", { type: "button", role: "menuitem", className: "feed-chat-launcher__option", disabled: isDisabled, onClick: () => {
                        void onSelect(bucket);
                    }, children: [_jsx("span", { children: bucket.label }), isPending ? (_jsx(FaIcon, { icon: faSpinnerThird, spin: true, className: "feed-chat-launcher__option-spinner" })) : undefined] }, bucket.id));
            })] }));
});
